import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface AnalyticsFilters {
  dateFrom?: string;
  dateTo?: string;
  excludeOperators?: boolean;
  groupBy?: 'day' | 'week' | 'month';
}

export interface AnalyticsSummary {
  dateFrom: string;
  dateTo: string;
  uniqueUsers: number;
  totalConnections: number;
  avgConnectionsPerUser: number;
  avgSessionDurationMinutes: number;
  peakHour: string;
}

export interface CountryData {
  country: string;
  uniqueUsers: number;
  totalConnections: number;
  percentage: number;
}

export interface AnalyticsByCountry {
  dateFrom: string;
  dateTo: string;
  countries: CountryData[];
}

export interface DeviceTypeData {
  deviceType: string;
  uniqueUsers: number;
  totalConnections: number;
  percentage: number;
}

export interface OSData {
  os: string;
  uniqueUsers: number;
  totalConnections: number;
  percentage: number;
}

export interface BrowserData {
  browser: string;
  uniqueUsers: number;
  totalConnections: number;
  percentage: number;
}

export interface AnalyticsByDevice {
  dateFrom: string;
  dateTo: string;
  byDeviceType: DeviceTypeData[];
  byOS: OSData[];
  byBrowser: BrowserData[];
}

export interface SessionDistribution {
  range: string;
  count: number;
  percentage: number;
}

export interface TopDurationUser {
  userId: number;
  nombre: string;
  email: string;
  durationMinutes: number;
  connectionCount: number;
}

export interface AnalyticsBySessionDuration {
  dateFrom: string;
  dateTo: string;
  avgDurationMinutes: number;
  medianDurationMinutes: number;
  distribution: SessionDistribution[];
  topByDuration: TopDurationUser[];
}

export interface TrendData {
  period: string;
  uniqueUsers: number;
  totalConnections: number;
  avgDurationMinutes: number;
}

export interface AnalyticsTrends {
  dateFrom: string;
  dateTo: string;
  groupBy: 'day' | 'week' | 'month';
  data: TrendData[];
}

export interface HourData {
  hour: number;
  connections: number;
  uniqueUsers: number;
}

export interface AnalyticsPeakHours {
  dateFrom: string;
  dateTo: string;
  hours: HourData[];
  peakHour: {
    hour: number;
    connections: number;
    uniqueUsers: number;
  };
}

@Injectable({ providedIn: 'root' })
export class ConnectionAnalyticsService {
  private baseUrl = `${environment.apiNest}notifications/analytics`;

  constructor(private http: HttpClient) {}

  private buildParams(filters: AnalyticsFilters): HttpParams {
    let params = new HttpParams();
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);
    if (filters.excludeOperators !== undefined) {
      params = params.set('excludeOperators', String(filters.excludeOperators));
    }
    if (filters.groupBy) params = params.set('groupBy', filters.groupBy);
    return params;
  }

  getSummary(filters: AnalyticsFilters): Observable<AnalyticsSummary> {
    return this.http.get<AnalyticsSummary>(`${this.baseUrl}/summary`, {
      params: this.buildParams(filters),
    });
  }

  getByCountry(filters: AnalyticsFilters): Observable<AnalyticsByCountry> {
    return this.http.get<AnalyticsByCountry>(`${this.baseUrl}/by-country`, {
      params: this.buildParams(filters),
    });
  }

  getByDevice(filters: AnalyticsFilters): Observable<AnalyticsByDevice> {
    return this.http.get<AnalyticsByDevice>(`${this.baseUrl}/by-device`, {
      params: this.buildParams(filters),
    });
  }

  getBySessionDuration(filters: AnalyticsFilters): Observable<AnalyticsBySessionDuration> {
    return this.http.get<AnalyticsBySessionDuration>(`${this.baseUrl}/by-session-duration`, {
      params: this.buildParams(filters),
    });
  }

  getTrends(filters: AnalyticsFilters): Observable<AnalyticsTrends> {
    return this.http.get<AnalyticsTrends>(`${this.baseUrl}/trends`, {
      params: this.buildParams(filters),
    });
  }

  getPeakHours(filters: AnalyticsFilters): Observable<AnalyticsPeakHours> {
    return this.http.get<AnalyticsPeakHours>(`${this.baseUrl}/peak-hours`, {
      params: this.buildParams(filters),
    });
  }
}
